import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Sparkles, RefreshCw, Loader2, ChevronDown, AlertTriangle, ThumbsUp, TrendingUp, Lightbulb, FileInput } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (d) => { try { return format(parseISO(d), 'dd/MM/yyyy'); } catch { return d || ''; } };
// Retire les références [n] pour le texte inséré dans le bilan
export const sansRefs = (t) => (t || '').replace(/\s*\[\d+\]/g, '').trim();
export const listeTexte = (titre, items) => items?.length ? `${titre} :\n${items.map(i => `- ${sansRefs(i)}`).join('\n')}` : '';

export const texteSynthese = (s) => [sansRefs(s.resume), listeTexte('Points forts', s.points_forts), listeTexte('Axes de progrès', s.axes_progres), listeTexte('Recommandations', s.recommandations)].filter(Boolean).join('\n\n');

const Liste = ({ titre, items, icon: Icon, color, testId }) => items?.length > 0 && (
  <div data-testid={testId}>
    <p className={`text-xs font-semibold flex items-center gap-1.5 ${color}`}><Icon className="w-3.5 h-3.5" /> {titre}</p>
    <ul className="mt-1 space-y-1">{items.map((it, i) => <li key={i} className="text-sm text-slate-700 pl-4 relative before:content-['•'] before:absolute before:left-1 before:text-slate-400">{it}</li>)}</ul>
  </div>
);

const TYPE_LABEL = { observation: 'Observation', qcm: 'QCM', evaluation: 'Évaluation', alerte: 'Alerte', assiduite: 'Indicateurs', abandon: 'Abandon' };

// Synthèse IA individuelle d'un candidat (sources : observations, alertes, évaluations, assiduité) + insertion dans le bilan
export const SyntheseCandidatDialog = ({ open, onClose, dossierId, candidatId, candidatNom, onInsert }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const base = `${API_URL}/dossiers/${dossierId}/candidats/${candidatId}/synthese`;
  useEffect(() => { if (open) axios.get(base).then(r => setData(r.data)).catch(() => setData(null)); }, [open, base]);
  const generer = async () => {
    setLoading(true);
    try { const r = await axios.post(`${base}/generer`); setData(r.data); toast.success('Synthèse générée'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de la génération'); }
    finally { setLoading(false); }
  };
  const s = data?.synthese;
  const texteInsertion = () => texteSynthese(s);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="dialog-synthese-candidat">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-600" /> Synthèse individuelle IA — {candidatNom}</DialogTitle>
          <p className="text-xs text-slate-500">Générée à partir des observations individuelles, alertes, résultats d'évaluations et indicateurs d'assiduité ({data?.nb_sources ?? 0} source(s)).</p>
        </DialogHeader>
        {!s ? (
          <div className="text-center py-6 space-y-3">
            <p className="text-sm text-slate-500">Aucune synthèse générée pour ce candidat.</p>
            <Button onClick={generer} disabled={loading || !data} className="bg-purple-600 hover:bg-purple-700" data-testid="synthese-cand-generer">{loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération…</> : <><Sparkles className="w-4 h-4 mr-2" /> Générer la synthèse</>}</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-[11px] text-slate-400">Générée le {fmtDate(s.genere_le)} par {s.genere_par}{data?.obsolete && <span className="ml-2 text-amber-600 font-medium inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> de nouvelles données ont été ajoutées depuis</span>}</p>
              <Button size="sm" variant="outline" onClick={generer} disabled={loading} data-testid="synthese-cand-regenerer">{loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Régénérer</Button>
            </div>
            <div className="rounded-lg bg-purple-50 border border-purple-100 p-3" data-testid="synthese-cand-resume"><p className="text-sm text-purple-950 whitespace-pre-wrap leading-relaxed">{s.resume}</p></div>
            <Liste titre="Points forts" items={s.points_forts} icon={ThumbsUp} color="text-emerald-700" testId="synthese-cand-pf" />
            <Liste titre="Axes de progrès" items={s.axes_progres} icon={TrendingUp} color="text-amber-700" testId="synthese-cand-axes" />
            <Liste titre="Recommandations" items={s.recommandations} icon={Lightbulb} color="text-indigo-700" testId="synthese-cand-reco" />
            <div className="border-t pt-3">
              <button type="button" onClick={() => setShowSources(v => !v)} className="text-xs font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid="synthese-cand-toggle-sources">
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showSources ? 'rotate-180' : ''}`} /> Sources ({(data?.sources || []).length}) — les numéros [n] renvoient à ces entrées
              </button>
              {showSources && (
                <ol className="mt-2 space-y-2" data-testid="synthese-cand-sources">
                  {(data?.sources || []).map((src, i) => (
                    <li key={i} className="rounded-md border border-slate-200 bg-slate-50 p-2.5 text-xs">
                      <p className="font-semibold text-slate-700"><span className="font-mono text-purple-600 mr-1">[{i + 1}]</span>{src.date ? `${fmtDate(src.date)} — ` : ''}{src.titre}<span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-slate-200 text-slate-600">{TYPE_LABEL[src.type] || src.type}</span></p>
                      {src.texte && <p className="text-slate-700 mt-1 whitespace-pre-wrap">{src.texte}</p>}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        )}
        {s && onInsert && (
          <DialogFooter>
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { onInsert(texteInsertion()); onClose(); }} data-testid="synthese-cand-inserer"><FileInput className="w-3.5 h-3.5 mr-1" /> Insérer dans la synthèse individuelle du bilan</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
